/**
 * Shared audio plumbing + "did our sound actually come out?" telemetry.
 *
 * Why this module exists: every audible cue in the app (the coverage arpeggio, the rider
 * chime, the rider mp3s, remote notifications) can be silenced by the browser itself, and
 * until now we had no way of knowing. Chrome's autoplay policy hands back an AudioContext
 * born `suspended` and rejects `HTMLMediaElement.play()` with `NotAllowedError` until the
 * page has seen a real user gesture. A kiosk tablet that boots straight into the PWA and is
 * never touched sits exactly there: we schedule oscillators, we call play(), and nothing is
 * heard. Both signals used to be swallowed (`resume().catch(() => {})`, a `logger.warning`
 * that never reaches product telemetry), so a restaurant whose tablet is mute looked
 * identical to one that ignores the alerts.
 *
 * What we CANNOT observe from the web — do not try: the OS volume, the silent switch, or
 * whether a human was in earshot. There is no API for any of it, and `HTMLMediaElement.volume`
 * is only the element's relative gain inside the tab. The narrower question is observable and
 * actionable, and it is the only one this module answers: did the BROWSER let our audio out?
 *
 * Emits one forensic event, `fx_audio_state`, at most twice per session (see reportAudioState
 * for the dedup rule), so the volume is ~1-2 events per device per day.
 */
import Logger from '@/logger';
import { reportForensicEvent, getForensicSessionId } from '@/forensicEvents';

const logger = new Logger({ screen: 'audio' });

// Single shared AudioContext for synthesized cues. Creating one per play leaks audio
// threads (Chrome caps active contexts) and a kiosk-autostarted context is born
// `suspended`, so resume it lazily before use.
let sharedAudioContext = null;

// Dedup state, module-level on purpose: it must survive component re-renders, and it is keyed
// on the forensic session id so it resets by itself on sign-out/sign-in.
let dedupSession = null;
let reportedBlocked = false;
let reportedOk = false;

/**
 * The shared AudioContext for synthesized cues, or null when WebAudio is unavailable.
 * `source` ('coverage' | 'rider') labels the cue asking for it, for telemetry only —
 * the returned context is the same object for every caller.
 */
export function getSharedAudioContext(source) {
  const AC = window.AudioContext || window.webkitAudioContext;
  if (!AC) {
    reportAudioState({ blocked: true, reason: 'no_audio_api', contextState: null, source });
    return null;
  }
  if (!sharedAudioContext) {
    try {
      sharedAudioContext = new AC();
    } catch (e) {
      // Construction itself refused (no output device, per-page context cap): as mute as no API.
      reportAudioState({ blocked: true, reason: 'no_audio_api', contextState: null, source, error: e });
      return null;
    }
  }
  const state = sharedAudioContext.state;
  if (state === 'suspended') {
    // A suspended context freezes `currentTime`, so the notes the caller is about to schedule
    // do not play now: they either never play, or fire late whenever a gesture finally unlocks
    // the context. Either way the cue did not sound when the alert fired, which is what we
    // report. Deliberately verdicted here and not inside resume(): by spec that promise may
    // never settle while the page still lacks a user gesture, so waiting on it would report
    // nothing at all — the exact blind spot we are closing.
    reportAudioState({ blocked: true, reason: 'context_suspended', contextState: state, source });
    try {
      const resumed = sharedAudioContext.resume();
      if (resumed && typeof resumed.then === 'function') {
        resumed.then(() => {
          // Chrome resolves this even when the policy keeps the context suspended, so only a
          // context that really reached `running` counts as recovered.
          const after = sharedAudioContext ? sharedAudioContext.state : null;
          if (after === 'running') {
            reportAudioState({ blocked: false, reason: 'ok', contextState: after, source });
          }
        }).catch(err => {
          reportAudioState({
            blocked: true,
            reason: 'resume_failed',
            contextState: sharedAudioContext ? sharedAudioContext.state : null,
            source,
            error: err,
          });
        });
      }
    } catch (e) {
      reportAudioState({ blocked: true, reason: 'resume_failed', contextState: state, source, error: e });
    }
  } else if (state === 'closed') {
    // Never closed by us, but Android can tear a context down under memory pressure. Its own
    // reason instead of folding it into `context_suspended`: a closed context never recovers,
    // it needs a new one, so the two are not the same finding.
    reportAudioState({ blocked: true, reason: 'context_closed', contextState: state, source });
  } else {
    reportAudioState({ blocked: false, reason: 'ok', contextState: state, source });
  }
  return sharedAudioContext;
}

/**
 * `HTMLMediaElement.play()` resolved — the mp3 is audible as far as the browser is concerned.
 */
export function reportAudioPlaybackOk(source) {
  reportAudioState({ blocked: false, reason: 'ok', contextState: null, source });
}

/**
 * `HTMLMediaElement.play()` was rejected (autoplay policy => `NotAllowedError`) or threw.
 */
export function reportAudioPlaybackBlocked(source, error) {
  reportAudioState({ blocked: true, reason: 'play_rejected', contextState: null, source, error });
}

/**
 * Emit `fx_audio_state` — at most once per outcome per session, so at most two events:
 * the first block and the first success, in whichever order they happen.
 *
 * Why report the success too: a count of blocks with no denominator is uninterpretable —
 * "12 blocked tablets" means nothing without knowing how many emitted sound fine. And why
 * allow the second event: the blocked -> ok transition is the signature of the kiosk case
 * (mute until somebody touches the screen), and it is worth one extra event to see it.
 * Further repeats of an outcome already reported add nothing and would multiply by every
 * alert of the shift, so they are dropped; the first `reason` of each outcome wins.
 */
function reportAudioState({ blocked, reason, contextState, source, error }) {
  try {
    // Before login the forensic lane is a no-op, and a sign-out/sign-in mints a new
    // session_id. Keying the dedup on that id means we neither burn the session's single
    // report on a pre-login attempt, nor stay silent for the rest of the day after the
    // tablet changes device.
    const session = getForensicSessionId();
    if (!session) return;
    if (session !== dedupSession) {
      dedupSession = session;
      reportedBlocked = false;
      reportedOk = false;
    }
    if (blocked ? reportedBlocked : reportedOk) return;
    if (blocked) reportedBlocked = true;
    else reportedOk = true;
    reportForensicEvent('audio_state', {
      blocked,
      reason,
      // The state of the AudioContext this attempt used — null for the `<audio>` element
      // path, which does not use one.
      context_state: contextState || null,
      source,
      error_name: (error && error.name) ? error.name : null, // name only: no messages, no stacks
    });
  } catch (e) {
    // Telemetry must never break the audio it is measuring.
    try { logger.console('audio_state telemetry failed', e); } catch (err) { /* noop */ }
  }
}
